import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import customBookingFormFieldOptionObject from '@/entities/customBookingFormFieldOption/interface'

import {
  ALL_FIELDS_QUERY,
  SINGLE_FIELD_QUERY,
  FIELDS_DEPENDENTS_QUERY,
  CREATE_FIELD_MUTATION,
  UPDATE_FIELD_MUTATION,
  DELETE_FIELD_MUTATION,
  FORCE_DELETE_FIELD_MUTATION,
  ADD_FIELD_OPTIONS_MUTATION,
  REMOVE_FIELD_OPTIONS_MUTATION,
  CREATE_FIELD_OPTIONS_MUTATION,
  DELETE_FIELD_OPTIONS_MUTATION,
  UPDATE_FIELD_OPTIONS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const customBookingFormFieldRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_FIELD_QUERY, variables).then((data: any) => {
      return hydrateObject(data.field)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_FIELDS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.fields)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_FIELD_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.field),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_FIELDS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.fields),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(FIELDS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([FIELDS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_FIELD_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.field)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_FIELD_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.field)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_FIELD_MUTATION, variables).then((data: any) => {
      return data.deleteCustomBookingFormField
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_FIELD_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCustomBookingFormField
    })
  },

  assignOptions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        optionIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_FIELD_OPTIONS_MUTATION, variables)
  },

  unassignOptions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        optionIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_FIELD_OPTIONS_MUTATION, variables)
  },

  createOptions: async (id: string, items: Array<customBookingFormFieldOptionObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        options: items,
      },
    }
    return graphQLClient.request(CREATE_FIELD_OPTIONS_MUTATION, variables)
  },

  deleteOptions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        optionIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_FIELD_OPTIONS_MUTATION, variables)
  },

  updateOptionsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        optionIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_FIELD_OPTIONS_ORDER_MUTATION, variables)
  },
}

export default customBookingFormFieldRepository
